import { Inject, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

import { BehaviorSubject, EMPTY, interval, Subject } from 'rxjs';
import { auditTime, debounceTime, filter, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { LOCATION_TOKEN, UPDATE_SERVICE_AUTO_DELAY } from '@app/injection-tokens';
import { SummitNotificationService } from '@summit/services';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  protected notificationService: SummitNotificationService;
  protected disposing$ = new Subject();
  updateAvailable = new BehaviorSubject<boolean>(false);
  updateAvailableDebounced = false;

  constructor(
    private swUpdate: SwUpdate,
    private router: Router,
    @Inject(LOCATION_TOKEN) private locationObject: Location,
    @Inject(UPDATE_SERVICE_AUTO_DELAY) private DelayBeforeReload: number
  ) {
    this.notificationService = inject(SummitNotificationService);
    //console.log(`[UpdateService] constructor`, this.swUpdate.isEnabled);
    if (!this.swUpdate.isEnabled) {
      console.log('[UpdateService] Service Worker Update Service not enabled');
    } else {
      console.log('[UpdateService]Service Worker Update Service is enabled');

      // Handle version updates and installation failures
      this.swUpdate.versionUpdates.pipe(takeUntil(this.disposing$)).subscribe(async (evt) => {
        console.log(`[UpdateService] versionUpdates emitted an event ${evt.type}`);
        switch (evt.type) {
          case 'VERSION_READY':
            // Handle successful version update
            console.log('[UpdateService] Update available event:', {
              type: 'UPDATE_AVAILABLE',
              current: evt.currentVersion,
              available: evt.latestVersion
            });
            console.log('[UpdateService] New version detected via versionUpdates');
            this.updateAvailable.next(true);
            this.notificationService.triggerToast('Update Available', null, 'info', null, null, null, true);
            break;

          case 'VERSION_INSTALLATION_FAILED':
            // Handle installation failure
            console.error('[UpdateService] Version installation failed:', evt.error);
            console.error('[UpdateService] Failed version hash:', evt.version.hash);

            // Attempt recovery by clearing caches and reloading
            try {
              console.log('[UpdateService] Attempting to recover from installation failure...');
              await this.clearServiceWorkerCaches();

              // Silently reload to recover
              setTimeout(() => {
                this.locationObject.reload();
              }, 1000);
            } catch (error) {
              console.error('[UpdateService] Recovery attempt failed:', error);
            }
            break;
        }
      });

      // Handle unrecoverable state - service worker is broken
      this.swUpdate.unrecoverable.pipe(takeUntil(this.disposing$)).subscribe((evt) => {
        console.error('[UpdateService] Service worker in unrecoverable state:', evt.reason);

        // Clear everything and reload silently
        setTimeout(async () => {
          await this.clearServiceWorkerCaches();
          this.locationObject.reload();
        }, 1000);
      });

      // Periodically check for updates every 10 minutes
      interval(10 * 60 * 1000)
        .pipe(
          startWith(0), // Check immediately on startup
          switchMap(() => this.swUpdate.checkForUpdate()),
          takeUntil(this.disposing$)
        )
        .subscribe({
          next: (updateFound) => {
            //console.log('[UpdateService] Looked for a new version');
            if (updateFound) {
              console.log('[UpdateService] New version detected via periodic check');
              this.updateAvailable.next(true);
            }
          },
          error: (err) => {
            console.error('[UpdateService] Error checking for updates:', err);
          }
        });
      this.updateAvailable
        .pipe(
          auditTime(16),
          debounceTime(this.DelayBeforeReload),
          filter((status) => status == true),
          takeUntil(this.disposing$)
        )
        .subscribe((debouncedUpdate) => {
          this.updateAvailableDebounced = debouncedUpdate;
        });
      this.router.events
        .pipe(
          //NavigationStart reloaded you on the page you were leaving
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.disposing$)
        )
        .subscribe(() => {
          if (this.updateAvailableDebounced) {
            this.update();
          }
        });
    }
  }

  /**
   * Clears all service worker caches to recover from corrupted state
   */
  private async clearServiceWorkerCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        console.log('[UpdateService] Clearing caches:', cacheNames);
        await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
        console.log('[UpdateService] All caches cleared');
      }

      // Attempt to unregister the service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
        console.log('[UpdateService] Service worker unregistered');
      }
    } catch (error) {
      console.error('[UpdateService] Error clearing caches:', error);
      throw error;
    }
  }

  async update() {
    await this.notificationService.showProgressSpinner(true, 'Updating');
    // Activate the new service worker version before reloading
    if (this.swUpdate.isEnabled) {
      try {
        const activated = await this.swUpdate.activateUpdate();
        console.log('[UpdateService] New version activated:', activated);
      } catch (err) {
        console.error('[UpdateService] Error activating update:', err);
      }
    }
    // console.log(`Cleaning up`);
    setTimeout(() => {
      this.cleanup();
      this.locationObject.reload();
    }, 1000);
  }

  cleanup() {
    this.disposing$.next(EMPTY);
    this.disposing$.complete();
  }

  OnDestroy() {
    this.cleanup();
  }

  logStart() {
    // console.log(`[UpdateService] started`);
  }
}
